import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import GarageCard from '../garage-card'
import { isClusterFeature, isValidHoverPoint } from './helpers'
import type { GaragesProps, MarkerProps } from './types'

export default function Garages({
    map,
    clusters,
    supercluster,
    selectedId,
    setSelectedId,
    hoverPoint,
    setHoverPoint,
    searchLocationPoint,
    loadingGarageAvailability,
    currency,
    navigateToGarage,
}: GaragesProps) {

    const { t } = useTranslation()

    return (
        <>
            {hoverPoint?.id &&
                <Marker
                    key={'hover-dot'}
                    map={map}
                    position={{
                        lat: hoverPoint.lat,
                        lng: hoverPoint.lng,
                    }}
                    zIndex={1}
                    onClick={() => {}}
                >
                    <div className={`
                        hover-dot
                    `}>
                    </div>
                </Marker>
            }
            {isValidHoverPoint(searchLocationPoint) &&
                <Marker
                    key={'search-location-dot'}
                    map={map}
                    position={{
                        lat: searchLocationPoint.lat,
                        lng: searchLocationPoint.lng,
                    }}
                    zIndex={15}
                    onClick={() => {}}
                >
                    <div className={`
                        search-location-dot
                    `}>
                    </div>
                </Marker>
            }
            {clusters.map(cluster => {
                if (isClusterFeature(cluster)) {
                    const [longitude, latitude] = cluster.geometry.coordinates
                    return (
                        <Marker
                            key={'cluster'+cluster.properties.cluster_id}
                            map={map}
                            position={{
                                lat: latitude,
                                lng: longitude,
                            }}
                            onClick={() => {
                                const expansionZoom = Math.min(
                                    supercluster.getClusterExpansionZoom(cluster.id),
                                    17,
                                )
                                let additionalZoom = 0
                                if (cluster.properties.point_count > 2) {
                                    additionalZoom = 1
                                }
                                if (cluster.properties.point_count > 5) {
                                    additionalZoom = 2
                                }
                                if (cluster.properties.point_count > 19) {
                                    additionalZoom = 3
                                }
                                map.setZoom(expansionZoom + additionalZoom)
                                map.panTo({ lat: latitude, lng: longitude })
                            }}
                            zIndex={1}
                        >
                            <div className={`
                                cluster
                                ${cluster.properties.point_count > 9 ? 'cluster-lg' : ''}
                                ${cluster.properties.point_count > 19 ? 'cluster-xl' : ''}
                                ${!cluster.properties.available && !loadingGarageAvailability ? 'cluster-unavailable' : ''}
                            `}>
                                <span>{cluster.properties.point_count}</span>
                            </div>
                        </Marker>
                    )
                }
                const garage = cluster.properties.garage
                const garagePriceCzk = garage.amountCentsCzk != null ? garage.amountCentsCzk / 100 : '---'
                const garagePriceEur = garage.amountCentsEur != null ? garage.amountCentsEur / 100 : '---'
                const garagePrice = currency === 'eur' ? `~€ ${garagePriceEur}` : `${garagePriceCzk} Kč`
                return (
                    <Marker
                        key={`garage${garage.id}`}
                        map={map}
                        position={{
                            lat: garage.lat,
                            lng: garage.lng,
                        }}
                        onClick={() => {
                            setSelectedId({
                                id: garage.id,
                                lat: garage.lat,
                                lng: garage.lng,
                            })
                            // Center card on mobile
                            if (window.innerWidth < 900) {
                                map.panTo({ lat: garage.lat, lng: garage.lng })
                            }
                        }}
                        zIndex={
                            selectedId?.id === garage.id ? 20 :
                                garage.available ? 10 : 5
                        }
                    >
                        <div
                            className={`
                                price-tag
                                ${(!loadingGarageAvailability && !garage.available) ? 'price-tag-unavailable' : ''}
                                ${hoverPoint?.id === garage.id ? 'price-tag-hover' : ''}
                                ${selectedId?.id === garage.id ? 'price-tag-card' : ''}
                            `}
                            onMouseEnter={() =>setHoverPoint({
                                id: garage.id,
                                lat: garage.lat,
                                lng: garage.lng,
                            })}
                            onMouseLeave={() =>setHoverPoint(null)}
                        >
                            {selectedId?.id !== garage.id &&
                                <span>{loadingGarageAvailability || garage.available ? garagePrice :
                                    garage.timeRangeValid !== true ? '—' :
                                    garage.availableLaterAt ? t('general.fromTime', {
                                        time: garage.availableLaterAt,
                                    }) : t('general.occupiedShort')}
                                </span>
                            }
                            {selectedId?.id === garage.id &&
                                <GarageCard
                                    garage={garage}
                                    availableLaterAt={garage.availableLaterAt}
                                    loadingGarageAvailability={loadingGarageAvailability}
                                    onMap={true}
                                    setSelectedId={setSelectedId}
                                    setHoverPoint={setHoverPoint}
                                    currency={currency}
                                    navigateToGarage={navigateToGarage}
                                />
                            }
                        </div>
                    </Marker>
                )
            })}
        </>
    )
}

function Marker({
    map,
    children,
    position,
    zIndex,
    onClick,
}: MarkerProps) {
    const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null)
    const [container, setContainer] = useState<HTMLDivElement | null>(null)

    useEffect(() => {
        if (container || markerRef.current) return

        const markerContainer = document.createElement('div')
        setContainer(markerContainer)

        markerRef.current = new google.maps.marker.AdvancedMarkerElement({
            position,
            content: markerContainer,
            zIndex,
        })
    }, [container, position, zIndex])

    useEffect(() => {
        const marker = markerRef.current
        if (!container || !marker) return

        marker.position = position
        marker.zIndex = zIndex
        marker.map = map
        const listener = marker.addListener('click', onClick)

        return () => listener.remove()
    }, [container, map, onClick, position, zIndex])

    useEffect(() => {
        return () => {
            if (markerRef.current) {
                markerRef.current.map = null
                markerRef.current = null
            }
            setContainer(null)
        }
    }, [])

    return container ? createPortal(children, container) : null
}
